import { create } from 'zustand';

import { getVaultLinks, type VaultFileAvailability } from '@shared/lib/vault-links';

export type VaultFileStatus = 'unknown' | 'available' | 'no-access' | 'removed';

export const VAULT_FOCUS_RECHECK_MS = 60_000;

interface VaultAvailabilityState {
  statuses: Record<string, VaultFileStatus>;
}

export const useVaultAvailabilityStore = create<VaultAvailabilityState>()(() => ({
  statuses: {},
}));

const finalIds = new Set<string>();
const inFlight = new Map<string, Promise<VaultFileStatus>>();
const trackedCounts = new Map<string, number>();
let generation = 0;
let lastSweepAt = 0;
let focusListenerInstalled = false;

function setStatus(fileId: string, status: VaultFileStatus): void {
  useVaultAvailabilityStore.setState((state) => ({
    statuses: { ...state.statuses, [fileId]: status },
  }));
}

export function checkVaultFile(
  fileId: string,
  options?: { force?: boolean },
): Promise<VaultFileStatus> {
  const links = getVaultLinks();
  if (!links) return Promise.resolve('unknown');

  const cached = useVaultAvailabilityStore.getState().statuses[fileId];
  if (!options?.force && finalIds.has(fileId) && cached !== undefined) {
    return Promise.resolve(cached);
  }

  const pending = inFlight.get(fileId);
  if (pending) return pending;

  const requestGeneration = generation;
  const request = new Promise<VaultFileAvailability>((resolve) => {
    resolve(links.availability(fileId));
  }).then(
    (availability) => {
      if (requestGeneration === generation) {
        finalIds.add(fileId);
        setStatus(fileId, availability);
      }
      return availability;
    },
    (): VaultFileStatus => {
      if (requestGeneration !== generation) return 'unknown';
      const kept = useVaultAvailabilityStore.getState().statuses[fileId];
      if (finalIds.has(fileId) && kept !== undefined) return kept;
      finalIds.delete(fileId);
      setStatus(fileId, 'unknown');
      return 'unknown';
    },
  );
  const settled = request.finally(() => {
    if (inFlight.get(fileId) === settled) inFlight.delete(fileId);
  });
  inFlight.set(fileId, settled);
  return settled;
}

export function checkVaultFiles(fileIds: readonly (string | undefined)[]): void {
  for (const fileId of new Set(fileIds)) {
    if (fileId !== undefined) void checkVaultFile(fileId);
  }
}

function onWindowFocus(): void {
  const now = Date.now();
  if (now - lastSweepAt < VAULT_FOCUS_RECHECK_MS) return;
  lastSweepAt = now;
  for (const fileId of trackedCounts.keys()) {
    void checkVaultFile(fileId, { force: true });
  }
}

function installFocusListener(): void {
  if (focusListenerInstalled) return;
  focusListenerInstalled = true;
  if (lastSweepAt === 0) lastSweepAt = Date.now();
  window.addEventListener('focus', onWindowFocus);
}

function removeFocusListener(): void {
  if (!focusListenerInstalled) return;
  focusListenerInstalled = false;
  window.removeEventListener('focus', onWindowFocus);
}

export function trackVaultFile(fileId: string): () => void {
  trackedCounts.set(fileId, (trackedCounts.get(fileId) ?? 0) + 1);
  installFocusListener();
  const trackGeneration = generation;
  let released = false;
  return () => {
    if (released || trackGeneration !== generation) return;
    released = true;
    const count = (trackedCounts.get(fileId) ?? 0) - 1;
    if (count > 0) trackedCounts.set(fileId, count);
    else trackedCounts.delete(fileId);
    if (trackedCounts.size === 0) removeFocusListener();
  };
}

export function resetVaultAvailability(): void {
  generation += 1;
  finalIds.clear();
  inFlight.clear();
  trackedCounts.clear();
  removeFocusListener();
  lastSweepAt = 0;
  useVaultAvailabilityStore.setState({ statuses: {} });
}
